import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { MdAddPhotoAlternate, MdClose } from 'react-icons/md';
import { Tooltip } from '@aibuild/ui';
import { LOGO_ACCEPT } from './appLogo';
import { useAppLogo } from './useAppLogo';
import { LogoImage, RemoveButton, Tile, TileButton } from './AppLogoTile.styled';

interface AppLogoTileProps {
  readonly appKey: string;
  readonly logo: string | null;
  readonly disabled?: boolean;
}

const AppLogoTile: React.FC<AppLogoTileProps> = ({ appKey, logo, disabled = false }) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, remove, busy } = useAppLogo(appKey);
  const label = logo
    ? intl.formatMessage({ id: 'apps.builder.logo.change', defaultMessage: 'Change the app logo' })
    : intl.formatMessage({ id: 'apps.builder.logo.add', defaultMessage: 'Add an app logo' });

  return (
    <Tile data-testid="app-builder__logo">
      <Tooltip
        content={intl.formatMessage({
          id: 'apps.builder.logo.hint',
          defaultMessage: 'App logo: PNG or SVG, white or black on transparent, square 512 × 512 px or a wordmark up to 6:1.',
        })}
        portal
      >
        <TileButton
          type="button"
          $filled={Boolean(logo)}
          aria-label={label}
          disabled={disabled || busy}
          aria-busy={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="app-builder__logo-upload"
        >
          {logo ? <LogoImage src={logo} alt="" /> : <MdAddPhotoAlternate size={20} aria-hidden="true" />}
        </TileButton>
      </Tooltip>
      {logo && !disabled && (
        <RemoveButton
          type="button"
          aria-label={intl.formatMessage({ id: 'apps.builder.logo.remove', defaultMessage: 'Remove the app logo' })}
          disabled={busy}
          onClick={remove}
          data-testid="app-builder__logo-remove"
        >
          <MdClose size={12} aria-hidden="true" />
        </RemoveButton>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={LOGO_ACCEPT}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) upload(file);
        }}
        data-testid="app-builder__logo-input"
      />
    </Tile>
  );
};

export default AppLogoTile;
